
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { CandidateComment } from "@/types/candidate";

export const useCandidateComments = (id: string | undefined): {
  comments: CandidateComment[];
  handleAddComment: (content: string) => Promise<void>;
  handleUpdateComment: (commentId: string, content: string) => Promise<void>;
  handleDeleteComment: (commentId: string) => Promise<void>;
} => {
  const [comments, setComments] = useState<CandidateComment[]>([]);

  const fetchComments = async () => {
    if (!id) return;

    try {
      const { data: commentsData, error: commentsError } = await supabase
        .from('candidate_comments')
        .select('*')
        .eq('candidate_id', id)
        .order('created_at', { ascending: false });

      if (commentsError) {
        console.error("Error fetching comments:", commentsError);
      } else {
        setComments(commentsData as CandidateComment[]);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [id]);

  const handleAddComment = async (content: string) => {
    if (!id || !content.trim()) return;

    try {
      const { data, error } = await supabase
        .from('candidate_comments')
        .insert({
          candidate_id: id,
          content,
          created_by: "Current User" // In a real app, this would come from authentication
        })
        .select();

      if (error) throw error;

      toast({
        title: "Comment added",
        description: "Your comment has been added successfully",
      });

      if (data) {
        setComments(prev => [data[0] as CandidateComment, ...prev]);
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      toast({
        title: "Error adding comment",
        description: "Could not add your comment",
        variant: "destructive",
      });
    }
  };

  const handleUpdateComment = async (commentId: string, content: string) => {
    if (!content.trim()) return;

    try {
      const { data, error } = await supabase
        .from('candidate_comments')
        .update({ 
          content,
          updated_at: new Date().toISOString()
        })
        .eq('id', commentId)
        .select();

      if (error) throw error;

      toast({
        title: "Comment updated",
        description: "Your comment has been updated successfully",
      });

      if (data) {
        setComments(prev => 
          prev.map(comment => 
            comment.id === commentId ? (data[0] as CandidateComment) : comment
          )
        );
      }
    } catch (error) {
      console.error("Error updating comment:", error);
      toast({
        title: "Error updating comment",
        description: "Could not update your comment",
        variant: "destructive",
      });
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      const { error } = await supabase
        .from('candidate_comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      toast({
        title: "Comment deleted",
        description: "The comment has been deleted successfully",
      });

      // Update local state
      setComments(prev => prev.filter(comment => comment.id !== commentId));
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast({
        title: "Error deleting comment",
        description: "Could not delete the comment",
        variant: "destructive",
      });
    }
  };

  return { 
    comments, 
    handleAddComment,
    handleUpdateComment,
    handleDeleteComment
  };
};
